/**
 * Breadcrumb molecule - navigation breadcrumb trail
 * Provides consistent navigation context across pages
 */

import {
  BreadcrumbItem,
  BreadcrumbLink,
  Breadcrumb as ChakraBreadcrumb,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';
import { FiChevronRight, FiHome } from 'react-icons/fi';
import { BaseComponentProps } from '../../types';

export interface IBreadcrumbItem {
  /** Item label */
  label: string;
  /** Item link href */
  href?: string;
  /** Whether item is current page */
  isCurrent?: boolean;
  /** Click handler for item */
  onClick?: () => void;
}

export interface BreadcrumbProps extends BaseComponentProps {
  /** Breadcrumb items */
  items: IBreadcrumbItem[];
  /** Whether to show home icon for first item */
  showHomeIcon?: boolean;
  /** Separator between items */
  separator?: React.ReactElement;
  /** Maximum number of items to show before truncation */
  maxItems?: number;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  showHomeIcon = true,
  separator = <FiChevronRight />,
  maxItems,
  className,
  testId,
}) => {
  const linkColor = useColorModeValue('gray.600', 'gray.400');
  const currentColor = useColorModeValue('gray.800', 'gray.100');

  // Handle truncation if maxItems is specified
  const displayItems =
    maxItems && items.length > maxItems
      ? [
          ...items.slice(0, 1), // Always show first item
          { label: '...', href: undefined }, // Ellipsis
          ...items.slice(-(maxItems - 2)), // Show last few items
        ]
      : items;

  return (
    <ChakraBreadcrumb
      spacing="8px"
      separator={separator}
      className={className}
      data-testid={testId}
    >
      {displayItems.map((item, index) => {
        const isFirst = index === 0;
        const isLast = index === displayItems.length - 1;

        return (
          <BreadcrumbItem
            key={`${item.label}-${index}`}
            isCurrentPage={item.isCurrent || isLast}
          >
            <BreadcrumbLink
              href={item.href}
              onClick={item.onClick}
              color={item.isCurrent || isLast ? currentColor : linkColor}
              fontWeight={item.isCurrent || isLast ? 'semibold' : 'normal'}
              _hover={
                item.isCurrent || isLast ? {} : { textDecoration: 'underline' }
              }
              cursor={item.isCurrent || isLast ? 'default' : 'pointer'}
              display="flex"
              alignItems="center"
              gap={2}
            >
              {isFirst && showHomeIcon && <FiHome size={14} />}
              {item.label}
            </BreadcrumbLink>
          </BreadcrumbItem>
        );
      })}
    </ChakraBreadcrumb>
  );
};
